import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { XMarkIcon, PhotoIcon, XCircleIcon, InformationCircleIcon, PlusIcon } from '@heroicons/react/24/outline';
import { ticketingAPI } from '../api/ticketing.api';
import type { TicketCategory, TicketPriority, TicketSource, CreateTicketData } from '../types/ticketing.types';
import './TicketModal.css';

/**
 * Suggested tags per category name (case-insensitive) and/or source.
 * When a matching category or source is selected, these tags are shown as clickable chips.
 */
const SUGGESTED_TAGS: Record<string, string[]> = {
  // By category name (lowercase)
  'revelo': ['Plan de tranche', 'Amiante', 'Inventaire', 'Audit énergétique', 'PEB', 'Autre produit'],
  // By source (prefixed with source:)
  'source:revelo': ['Plan de tranche', 'Amiante', 'Inventaire', 'Audit énergétique', 'PEB', 'Autre produit'],
};

interface Attachment {
  id: string;
  name: string;
  type: string;
  data: string; // base64
  size: number;
}

interface TicketModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  categories: TicketCategory[];
}

export const TicketModal: React.FC<TicketModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  categories,
}) => {
  const [formData, setFormData] = useState<CreateTicketData>({
    title: '',
    description: '',
    priority: 'medium',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [isClosing, setIsClosing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const formRef = useRef<HTMLFormElement>(null);

  // Handle close with animation
  const handleClose = useCallback(() => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
      setIsClosing(false);
    }, 250);
  }, [onClose]);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setFormData({ title: '', description: '', priority: 'medium', tags: [] });
      setAttachments([]);
      setError('');
      setCustomTagInput('');
    }
  }, [isOpen]);

  // Handle paste event for screenshots
  useEffect(() => {
    const handlePaste = (e: ClipboardEvent) => {
      if (!isOpen) return;

      const items = e.clipboardData?.items;
      if (!items) return;

      for (const item of items) {
        if (item.type.startsWith('image/')) {
          e.preventDefault();
          const file = item.getAsFile();
          if (file) {
            processFile(file);
          }
          break;
        }
      }
    };

    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, [isOpen]);

  const processFile = useCallback((file: File) => {
    if (!file.type.startsWith('image/')) {
      setError('Seules les images sont acceptées');
      return;
    }

    if (file.size > 5 * 1024 * 1024) { // 5MB limit
      setError('L\'image ne doit pas dépasser 5MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const base64 = e.target?.result as string;
      const newAttachment: Attachment = {
        id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        name: file.name || `screenshot-${Date.now()}.png`,
        type: file.type,
        data: base64,
        size: file.size,
      };
      setAttachments(prev => [...prev, newAttachment]);
      setError('');
    };
    reader.readAsDataURL(file);
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      Array.from(files).forEach(processFile);
    }
    // Reset input value to allow selecting the same file again
    e.target.value = '';
  };

  const handleRemoveAttachment = (id: string) => {
    setAttachments(prev => prev.filter(a => a.id !== id));
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const files = e.dataTransfer.files;
    if (files) {
      Array.from(files).forEach(processFile);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      setError('Le titre est requis');
      return;
    }

    try {
      setLoading(true);
      setError('');
      // Include attachments in the ticket data
      const ticketData = {
        ...formData,
        attachments: attachments.map(a => ({
          name: a.name,
          type: a.type,
          data: a.data,
          size: a.size,
        })),
      };
      await ticketingAPI.createTicket(ticketData);
      onSuccess();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erreur lors de la creation du ticket');
    } finally {
      setLoading(false);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Check if selected category is "bug"
  const isBugCategory = useMemo(() => {
    if (!formData.category_id) return false;
    const selectedCategory = categories.find(c => c.id === formData.category_id);
    return selectedCategory?.name?.toLowerCase() === 'bug';
  }, [formData.category_id, categories]);

  // Get suggested tags based on selected category and/or source
  const suggestedTags = useMemo(() => {
    const tags = new Set<string>();
    if (formData.category_id) {
      const selectedCategory = categories.find(c => c.id === formData.category_id);
      const catName = selectedCategory?.name?.toLowerCase();
      if (catName && SUGGESTED_TAGS[catName]) {
        SUGGESTED_TAGS[catName].forEach(t => tags.add(t));
      }
    }
    if (formData.source) {
      const sourceKey = `source:${formData.source}`;
      if (SUGGESTED_TAGS[sourceKey]) {
        SUGGESTED_TAGS[sourceKey].forEach(t => tags.add(t));
      }
    }
    return Array.from(tags);
  }, [formData.category_id, formData.source, categories]);

  const toggleTag = useCallback((tag: string) => {
    setFormData(prev => {
      const currentTags = prev.tags || [];
      const exists = currentTags.includes(tag);
      return {
        ...prev,
        tags: exists ? currentTags.filter(t => t !== tag) : [...currentTags, tag],
      };
    });
  }, []);

  const [customTagInput, setCustomTagInput] = useState('');

  const addCustomTag = useCallback(() => {
    const tag = customTagInput.trim();
    if (!tag) return;
    setFormData(prev => {
      const currentTags = prev.tags || [];
      if (currentTags.includes(tag)) return prev;
      return { ...prev, tags: [...currentTags, tag] };
    });
    setCustomTagInput('');
  }, [customTagInput]);

  if (!isOpen) return null;

  return (
    <div className={`ticket-create-overlay ${isClosing ? 'closing' : ''}`} onClick={handleClose}>
      <div className={`ticket-create-panel ${isClosing ? 'closing' : ''}`} onClick={(e) => e.stopPropagation()}>
        <div className="ticket-create-header">
          <h2>Nouveau ticket</h2>
          <button className="ticket-create-close" onClick={handleClose}>
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="ticket-create-body">
          {error && <div className="ticket-create-error">{error}</div>}

          <div className="ticket-form-group">
            <label htmlFor="title">Titre *</label>
            <input
              id="title"
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="Titre du ticket"
              required
            />
          </div>

          <div className="ticket-form-group">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Description detaillee du probleme ou de la demande"
              rows={4}
            />
          </div>

          <div className="ticket-form-row">
            <div className="ticket-form-group">
              <label htmlFor="category">Categorie</label>
              <select
                id="category"
                value={formData.category_id || ''}
                onChange={(e) => setFormData({ ...formData, category_id: e.target.value || undefined })}
              >
                <option value="">Selectionner...</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="ticket-form-group">
              <label htmlFor="priority">Priorite</label>
              <select
                id="priority"
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: e.target.value as TicketPriority })}
              >
                <option value="low">Basse</option>
                <option value="medium">Moyenne</option>
                <option value="high">Haute</option>
                <option value="urgent">Urgente</option>
              </select>
            </div>

            <div className="ticket-form-group">
              <label htmlFor="source">Application</label>
              <select
                id="source"
                value={formData.source || ''}
                onChange={(e) => setFormData({ ...formData, source: e.target.value as TicketSource || undefined })}
              >
                <option value="">Selectionner...</option>
                <option value="webapp">WebApp</option>
                <option value="pickit">PickIt</option>
                <option value="revelo">Revelo</option>
              </select>
            </div>
          </div>

          {/* Bug category helper */}
          {isBugCategory && (
            <div className="ticket-bug-helper">
              <InformationCircleIcon className="ticket-bug-helper-icon" />
              <div className="ticket-bug-helper-content">
                <strong>Pour un bon rapport de bug, merci d'inclure :</strong>
                <ul>
                  <li>Sur quel onglet/page vous avez vu le bug</li>
                  <li>Une capture d'ecran (collez avec Ctrl+V)</li>
                  <li>Ce que vous attendiez comme resultat</li>
                  <li>Ce que vous avez observe a la place</li>
                </ul>
              </div>
            </div>
          )}

          {/* Suggested tags based on category/source */}
          {(suggestedTags.length > 0 || (formData.tags && formData.tags.length > 0)) && (
            <div className="ticket-form-group">
              <label>Sous-catégorie / Tags</label>
              <div className="ticket-tags-container">
                {suggestedTags.map((tag) => (
                  <button
                    key={tag}
                    type="button"
                    className={`ticket-tag-chip ${formData.tags?.includes(tag) ? 'active' : ''}`}
                    onClick={() => toggleTag(tag)}
                  >
                    {tag}
                  </button>
                ))}
                {/* Show selected custom tags that aren't in suggestions */}
                {formData.tags?.filter(t => !suggestedTags.includes(t)).map((tag) => (
                  <button
                    key={tag}
                    type="button"
                    className="ticket-tag-chip active"
                    onClick={() => toggleTag(tag)}
                  >
                    {tag} ×
                  </button>
                ))}
              </div>
              <div className="ticket-custom-tag-row">
                <input
                  type="text"
                  value={customTagInput}
                  onChange={(e) => setCustomTagInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustomTag(); } }}
                  placeholder="Ajouter un tag personnalisé..."
                  className="ticket-custom-tag-input"
                />
                <button type="button" className="ticket-custom-tag-add" onClick={addCustomTag} disabled={!customTagInput.trim()}>
                  <PlusIcon className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          <div className="ticket-form-group">
            <label htmlFor="affaire_id">Numero d'affaire (optionnel)</label>
            <input
              id="affaire_id"
              type="number"
              value={formData.affaire_id || ''}
              onChange={(e) => setFormData({ ...formData, affaire_id: e.target.value ? parseInt(e.target.value) : undefined })}
              placeholder="Ex: 12345"
            />
          </div>

          {/* Screenshot/Attachment Upload */}
          <div className="ticket-form-group">
            <label>Captures d'ecran</label>
            <div
              className="ticket-attachment-dropzone"
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
              <PhotoIcon className="w-8 h-8 text-gray-400" />
              <p className="ticket-attachment-hint">
                Cliquez, glissez-deposez ou collez (Ctrl+V) une image
              </p>
              <span className="ticket-attachment-info">PNG, JPG, GIF jusqu'a 5MB</span>
            </div>

            {/* Attachment Preview */}
            {attachments.length > 0 && (
              <div className="ticket-attachments-preview">
                {attachments.map((attachment) => (
                  <div key={attachment.id} className="ticket-attachment-item">
                    <img src={attachment.data} alt={attachment.name} className="ticket-attachment-thumb" />
                    <div className="ticket-attachment-info-text">
                      <span className="ticket-attachment-name">{attachment.name}</span>
                      <span className="ticket-attachment-size">{formatFileSize(attachment.size)}</span>
                    </div>
                    <button
                      type="button"
                      className="ticket-attachment-remove"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveAttachment(attachment.id);
                      }}
                    >
                      <XCircleIcon className="w-5 h-5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          </div>

          <div className="ticket-create-footer">
            <button type="button" className="ticket-btn ticket-btn-secondary" onClick={handleClose}>
              Annuler
            </button>
            <button type="submit" className="ticket-btn ticket-btn-primary" disabled={loading}>
              {loading ? 'Creation...' : 'Creer le ticket'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
